// 2026-06-26: Phase 14 — Heat Treat Cycles (NADCAP commodity module — AC7102 + AMS2750H)
// Captures furnace runs with pyrometry profile, SAT/TUS recency, QA sign-off.
// First commodity per PRD Path A (recommended Tier 1).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import GavelIcon from '@mui/icons-material/Gavel';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

const FURNACES = ['PMI-G-HT-01','PMI-G-HT-02','PMI-G-HT-03','PMI-G-HT-04'];
const FURNACE_CLASSES = ['Class 1 (±3°C)','Class 2 (±6°C)','Class 3 (±8°C)','Class 4 (±14°C)','Class 5 (±28°C)','Class 6 (±56°C)'];
const ATMOSPHERES = ['Air','N2','Ar','H2','Vacuum','Endothermic','Exothermic'];

const STATUS_INFO = {
  COMPLETE_OK:    { color: BR.green, bg: '#dcfce7', label: '✓ ACCEPTED', icon: <VerifiedIcon fontSize="small" /> },
  COMPLETE_HOLD:  { color: BR.amber, bg: '#fef3c7', label: '⚠ ON HOLD', icon: <GavelIcon fontSize="small" /> },
  RUNNING:        { color: BR.orange, bg: '#fff7ed', label: '▶ RUNNING', icon: <LocalFireDepartmentIcon fontSize="small" /> },
  REJECTED:       { color: BR.red, bg: '#fee2e2', label: '✗ REJECTED', icon: <GavelIcon fontSize="small" /> },
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' +
         dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const emptyCycle = {
  id: '',
  cycle_no: '',
  furnace_id: '',
  furnace_class: '',
  load_no: '',
  wo_numbers: [],
  recipe_id: '',
  setpoint_c: '',
  soak_min: '',
  atmosphere: '',
  start_ts: '',
  end_ts: '',
  max_temp_c: '',
  last_tus_date: '',
  last_sat_date: '',
  operator_name: '',
  qa_signoff_by: '',
  qa_signoff_ts: '',
  status: 'RUNNING',
  notes: '',
};

export default function HeatTreatCycles() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyCycle);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await customerbasedshift(customerId, 'heat_treat_cycles');
      const v = Array.isArray(r?.[0]?.value) ? r[0].value : [];
      setItems(v);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = useMemo(() => {
    const out = { ALL: items.length, RUNNING: 0, COMPLETE_OK: 0, COMPLETE_HOLD: 0, REJECTED: 0 };
    items.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return (i.cycle_no || '').toLowerCase().includes(q)
          || (i.furnace_id || '').toLowerCase().includes(q)
          || (i.recipe_id || '').toLowerCase().includes(q)
          || (i.load_no || '').toLowerCase().includes(q);
    }).sort((a, b) => (new Date(b.start_ts) - new Date(a.start_ts)));
  }, [items, filter, search]);

  function openAdd() { setDraft({ ...emptyCycle, id: `htc-${Date.now()}` }); setEditOpen(true); }
  function openEdit(item) { setDraft({ ...emptyCycle, ...item }); setEditOpen(true); }

  async function save() {
    if (!draft.cycle_no || !draft.furnace_id) {
      Swal.fire({ icon: 'warning', title: 'Cycle # and furnace required', confirmButtonColor: BR.orange });
      return;
    }
    setSaving(true);
    try {
      const exists = items.findIndex(i => i.id === draft.id);
      const next = exists === -1 ? [...items, draft]
                                 : items.map((it, idx) => idx === exists ? draft : it);
      await shiftadd({ heat_treat_cycles: next }, customerId, 'SERVER_SCOPE');
      setItems(next); setEditOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                   icon: 'success', title: `${draft.cycle_no} saved` });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange });
    } finally { setSaving(false); }
  }

  async function signOff(item) {
    const { value: form } = await Swal.fire({
      title: `QA sign-off · ${item.cycle_no}`,
      html: `
        <div style="text-align:left; font-size:13px;">
          <div style="background:#fff7ed; padding:10px; border-radius:6px; margin-bottom:12px;">
            <strong>Furnace:</strong> ${item.furnace_id} (${item.furnace_class || 'class ?'})<br/>
            <strong>Recipe:</strong> ${item.recipe_id} · <strong>Setpoint:</strong> ${item.setpoint_c}°C ± ${(item.furnace_class || '').match(/\\d+/) || '?'}°C<br/>
            <strong>Soak:</strong> ${item.soak_min} min · <strong>Atm:</strong> ${item.atmosphere}<br/>
            <strong>Last TUS:</strong> ${item.last_tus_date || 'NOT RECORDED'} · <strong>Last SAT:</strong> ${item.last_sat_date || 'NOT RECORDED'}
          </div>
          <label style="font-weight:700; display:block; margin-bottom:6px;">Disposition</label>
          <select id="qa-disp" style="width:100%; padding:10px; font-size:14px; font-weight:600; border:1.5px solid #d1d5db; border-radius:6px;">
            <option value="COMPLETE_OK">✓ Accept — within spec</option>
            <option value="COMPLETE_HOLD">⚠ Hold — needs review</option>
            <option value="REJECTED">✗ Reject — out of spec</option>
          </select>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">QA name</label>
          <input id="qa-name" placeholder="Your name (sign-off)" style="width:100%; padding:9px 12px; font-size:14px; border:1.5px solid #d1d5db; border-radius:6px;"/>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">Note</label>
          <textarea id="qa-note" rows="2" style="width:100%; padding:8px; font-size:13px; border:1.5px solid #d1d5db; border-radius:6px; font-family:inherit;"></textarea>
        </div>
      `,
      icon: 'info', showCancelButton: true,
      confirmButtonText: 'Sign off', confirmButtonColor: BR.orange,
      cancelButtonText: 'Cancel', reverseButtons: true,
      preConfirm: () => {
        const disp = document.getElementById('qa-disp').value;
        const name = document.getElementById('qa-name').value.trim();
        const note = document.getElementById('qa-note').value.trim();
        if (!name) { Swal.showValidationMessage('QA name required for sign-off'); return false; }
        return { disp, name, note };
      },
    });
    if (!form) return;
    const next = items.map(i => i.id === item.id ? {
      ...i, status: form.disp,
      qa_signoff_by: form.name,
      qa_signoff_ts: new Date().toISOString(),
      notes: form.note ? `${i.notes || ''}\nQA: ${form.note}` : i.notes,
    } : i);
    await shiftadd({ heat_treat_cycles: next }, customerId, 'SERVER_SCOPE');
    setItems(next);
    Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                 icon: 'success', title: `Signed off by ${form.name}` });
  }

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>
            🔥 Heat Treat Cycles
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Pyrometry · recipe · TUS/SAT recency · QA sign-off · NADCAP AC7102 + AMS2750H
          </div>
        </div>
        <button onClick={openAdd}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '9px 16px', background: BR.orange, color: '#fff',
                          border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Record Cycle
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL', label: 'All cycles', color: BR.dark },
          { key: 'RUNNING', label: 'Running', color: BR.orange },
          { key: 'COMPLETE_OK', label: 'Accepted', color: BR.green },
          { key: 'COMPLETE_HOLD', label: 'On hold', color: BR.amber },
          { key: 'REJECTED', label: 'Rejected', color: BR.red },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
               style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`,
                        borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {totals[t.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)}
             placeholder="Search cycle #, furnace, recipe, load #…"
             style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BR.border}`,
                       borderRadius: 6, fontSize: 13, marginBottom: 10 }} />

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No cycles {filter !== 'ALL' ? `in ${filter} state` : 'yet'}. Click "Record Cycle" to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status','Cycle #','Furnace','Recipe','Setpoint','Soak','Atm','Started','Ended','Operator','QA','WO Loads',''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.RUNNING;
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.icon} {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{i.cycle_no}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{i.furnace_id}</div>
                      <div style={{ fontSize: 10, color: BR.muted }}>{i.furnace_class || '—'}</div>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: BR.muted }}>{i.recipe_id || '—'}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700 }}>{i.setpoint_c ? `${i.setpoint_c}°C` : '—'}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right' }}>{i.soak_min ? `${i.soak_min}m` : '—'}</td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{i.atmosphere || '—'}</td>
                    <td style={{ padding: '9px 10px', fontSize: 11 }}>{fmtDate(i.start_ts)}</td>
                    <td style={{ padding: '9px 10px', fontSize: 11 }}>{fmtDate(i.end_ts)}</td>
                    <td style={{ padding: '9px 10px' }}>{i.operator_name || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      {i.qa_signoff_by ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: BR.green }}>✓ {i.qa_signoff_by}</span>
                      ) : (
                        <button onClick={() => signOff(i)}
                                style={{ padding: '3px 8px', background: BR.orange, color: '#fff',
                                          border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>
                          Sign off
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      {(i.wo_numbers || []).slice(0, 2).map(wo => (
                        <Chip key={wo} label={wo} size="small"
                              sx={{ height: 18, fontSize: 9, fontWeight: 700, fontFamily: 'monospace',
                                     background: '#eff6ff', color: '#1e3a8a', border: '1px solid #93c5fd', marginRight: 0.3, marginBottom: 0.3 }} />
                      ))}
                      {(i.wo_numbers || []).length > 2 && (
                        <span style={{ fontSize: 10, color: BR.muted }}>+{i.wo_numbers.length - 2}</span>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(i)}>
                        <EditIcon fontSize="small" sx={{ color: '#111' }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#fff7ed', border: `1px solid #fed7aa`, borderRadius: 6 }}>
        <strong style={{ color: '#9a3412' }}>AMS2750H note:</strong> Furnace TUS (Temperature Uniformity Survey) and SAT (System Accuracy Test) recency are mandatory inputs. A cycle on a furnace with stale TUS/SAT must be flagged for QA review. Today this is recorded manually; future iteration will pull from the Calibration Register and the live IIoT TC stream and block sign-off automatically.
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {items.some(i => i.id === draft.id) ? `Edit cycle` : 'Record new heat treat cycle'}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <TextField label="Cycle # *" size="small" fullWidth
                       value={draft.cycle_no}
                       onChange={e => setDraft({ ...draft, cycle_no: e.target.value })}
                       placeholder="HT-2026-0042" />
            <TextField label="Furnace *" size="small" fullWidth select
                       value={draft.furnace_id}
                       onChange={e => setDraft({ ...draft, furnace_id: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {FURNACES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
            <TextField label="Furnace Class" size="small" fullWidth select
                       value={draft.furnace_class}
                       onChange={e => setDraft({ ...draft, furnace_class: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {FURNACE_CLASSES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField label="Recipe ID" size="small" fullWidth
                       value={draft.recipe_id}
                       onChange={e => setDraft({ ...draft, recipe_id: e.target.value })}
                       placeholder="HT-RCP-AGE-A2024" />
            <TextField label="Load #" size="small" fullWidth
                       value={draft.load_no}
                       onChange={e => setDraft({ ...draft, load_no: e.target.value })} />
            <TextField label="WO Numbers (comma-sep)" size="small" fullWidth
                       value={(draft.wo_numbers || []).join(', ')}
                       onChange={e => setDraft({ ...draft, wo_numbers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
            <TextField label="Setpoint (°C)" size="small" fullWidth type="number"
                       value={draft.setpoint_c}
                       onChange={e => setDraft({ ...draft, setpoint_c: e.target.value })} />
            <TextField label="Soak Time (min)" size="small" fullWidth type="number"
                       value={draft.soak_min}
                       onChange={e => setDraft({ ...draft, soak_min: e.target.value })} />
            <TextField label="Atmosphere" size="small" fullWidth select
                       value={draft.atmosphere}
                       onChange={e => setDraft({ ...draft, atmosphere: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {ATMOSPHERES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
            <TextField label="Start Time" size="small" fullWidth type="datetime-local"
                       value={draft.start_ts ? draft.start_ts.slice(0, 16) : ''}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, start_ts: e.target.value })} />
            <TextField label="End Time" size="small" fullWidth type="datetime-local"
                       value={draft.end_ts ? draft.end_ts.slice(0, 16) : ''}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, end_ts: e.target.value })} />
            <TextField label="Max Temp Captured (°C)" size="small" fullWidth type="number"
                       value={draft.max_temp_c}
                       onChange={e => setDraft({ ...draft, max_temp_c: e.target.value })} />
            <TextField label="Last TUS Date" size="small" fullWidth type="date"
                       value={draft.last_tus_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, last_tus_date: e.target.value })} />
            <TextField label="Last SAT Date" size="small" fullWidth type="date"
                       value={draft.last_sat_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, last_sat_date: e.target.value })} />
            <TextField label="Operator" size="small" fullWidth
                       value={draft.operator_name}
                       onChange={e => setDraft({ ...draft, operator_name: e.target.value })} />
            <TextField label="Status" size="small" fullWidth select
                       value={draft.status}
                       onChange={e => setDraft({ ...draft, status: e.target.value })}>
              <MenuItem value="RUNNING">▶ Running</MenuItem>
              <MenuItem value="COMPLETE_OK">✓ Accepted</MenuItem>
              <MenuItem value="COMPLETE_HOLD">⚠ On hold</MenuItem>
              <MenuItem value="REJECTED">✗ Rejected</MenuItem>
            </TextField>
            <TextField label="Notes" size="small" fullWidth multiline minRows={2}
                       value={draft.notes}
                       onChange={e => setDraft({ ...draft, notes: e.target.value })}
                       sx={{ gridColumn: '1 / span 3' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
                  sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
